import React, { useCallback, useEffect, useRef, useState } from 'react';
import { doc, setDoc, arrayUnion } from 'firebase/firestore';
import { db, messaging, getToken, onMessage } from '../firebase';
import { withTenant } from '../services/data';
import { colors, fonts, radius, spacing } from './theme';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
// RW-1248: env-only; hardcoded fallback removed. When REACT_APP_VAPID_KEY is
// unset we log once and skip FCM token registration entirely.
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || '';

let vapidWarned = false;
function warnMissingVapidKeyOnce() {
  if (vapidWarned) return;
  vapidWarned = true;
  console.warn('PushNotificationManager: REACT_APP_VAPID_KEY is not set — skipping FCM token registration (RW-1248).');
}

function dismissKey(uid) { return `notif_banner_dismissed_${uid}`; }

function setDismissedLocally(uid) {
  try { localStorage.setItem(dismissKey(uid), 'true'); } catch { /* */ }
}

async function setDismissedFirestore(uid) {
  try {
    await setDoc(doc(db, 'users', uid), withTenant('users', { notificationBannerDismissed: true }), { merge: true });
  } catch { /* silent */ }
}

// ---------------------------------------------------------------------------
// RW-2264 (RW-2258 Phase 3a): the permission + token flow, callable from the
// two places that may ask — the one earned-moment card after a first post
// (PushAskCard) and Settings → Push Notifications. The load-time banner this
// component used to render is gone (15 of 23 agents dismissed it; 0 tokens).
//
// Resolves the resulting Notification.permission ('granted' | 'denied' |
// 'default') or 'unsupported'. On 'granted' the FCM token is registered on
// users/{uid}.fcmTokens — the field functions/notifications/pushDispatch.js
// reads — and remembered per device for sign-out (RW-401).
// ---------------------------------------------------------------------------
export function pushSupported() {
  return typeof window !== 'undefined' && 'Notification' in window && !!messaging;
}

export function pushAskKey(uid) { return `reve_push_ask_${uid}`; }

export function hasAskedForPush(uid) {
  try { return localStorage.getItem(pushAskKey(uid)) === 'true'; } catch { return false; }
}

export function markAskedForPush(uid) {
  try { localStorage.setItem(pushAskKey(uid), 'true'); } catch { /* */ }
}

export async function requestPushPermission(uid) {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (uid) {
    // The old banner's "never again" mark — kept so a stored dismissal and a
    // granted permission mean the same thing to any reader of that field.
    setDismissedLocally(uid);
    setDismissedFirestore(uid);
  }
  let permission = window.Notification.permission;
  try {
    permission = await window.Notification.requestPermission();
  } catch {
    return permission;
  }
  if (permission !== 'granted' || !messaging || !uid) return permission;
  // RW-1248: no VAPID key configured — skip token registration.
  if (!VAPID_KEY) { warnMissingVapidKeyOnce(); return permission; }
  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await setDoc(doc(db, 'users', uid), withTenant('users', { fcmTokens: arrayUnion(token) }), { merge: true });
      // Persist this device's token so logout can remove just this one (RW-401)
      try { localStorage.setItem(`reve_fcm_token_${uid}`, token); } catch { /* silent */ }
    }
  } catch { /* token registration failed — the permission grant still stands */ }
  return permission;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  toastContainer: {
    position: 'fixed', top: spacing.lg, right: spacing.lg, zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: spacing.sm, pointerEvents: 'none',
  },
  toast: {
    pointerEvents: 'auto', background: colors.white, borderLeft: `4px solid ${colors.coral}`,
    borderRadius: radius.md, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    padding: `${spacing.sm} ${spacing.md}`, minWidth: 260, maxWidth: 340,
    fontFamily: fonts.body, cursor: 'pointer', animation: 'toastSlideIn 0.25s ease',
  },
  toastTitle: { fontWeight: 700, fontSize: 14, color: colors.noir, marginBottom: 2 },
  toastBody: { fontSize: 13, color: colors.gray3, lineHeight: 1.4 },
};

// ---------------------------------------------------------------------------
// Helper — inject keyframe animation once
// ---------------------------------------------------------------------------
let animationInjected = false;
function injectAnimation() {
  if (animationInjected || typeof document === 'undefined') return;
  animationInjected = true;
  const style = document.createElement('style');
  style.textContent = `@keyframes toastSlideIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }`;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Component — foreground push toasts only.
//
// RW-2264: no permission banner on load any more. The ask happens once, after
// an agent's first post (PushAskCard), and lives on under Settings → Push
// Notifications. `onBannerVisibilityChange` is still honoured (App.js offsets
// its layout by it) and now always reports false; `topOffset` is accepted and
// ignored for the same reason.
// ---------------------------------------------------------------------------
export default function PushNotificationManager({ user, onBannerVisibilityChange }) {
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});

  useEffect(() => {
    injectAnimation();
    // A permission granted elsewhere (Settings, the card, the browser's own
    // site settings) retires the old banner's dismissal field for good.
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted' && user?.uid) {
      setDismissedLocally(user.uid);
      setDismissedFirestore(user.uid);
    }
  }, [user]);

  useEffect(() => {
    if (typeof onBannerVisibilityChange === 'function') onBannerVisibilityChange(false);
  }, [onBannerVisibilityChange]);

  // Subscribe to foreground messages
  useEffect(() => {
    if (!messaging) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || 'New notification';
      const body = payload?.notification?.body || '';
      const id = `${Date.now()}-${Math.random()}`;
      setToasts((prev) => [...prev, { id, title, body }]);
      toastTimers.current[id] = setTimeout(() => dismissToast(id), 5000);
    });
    const timers = toastTimers.current;
    return () => { unsubscribe(); Object.values(timers).forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissToast = useCallback((id) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div style={S.toastContainer} aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} role="status" style={S.toast} onClick={() => dismissToast(t.id)}>
          <div style={S.toastTitle}>{t.title}</div>
          {t.body ? <div style={S.toastBody}>{t.body}</div> : null}
        </div>
      ))}
    </div>
  );
}
